// スクロール連動の演出
// - [data-reveal]   画面に入ったら data-in を付けて表示（種類は CSS 側で定義）
// - [data-stagger]  直下の子要素を順番に表示
// - [data-parallax] 親の枠の中で写真をスクロールに合わせてずらす（値は枠の高さに対する移動量）
// - [data-loop]     画面外ではループアニメーションを止める
// 状態はクラスではなく data 属性で持つ（React が className を書き換えても消えないように）

// 幕で隠した写真はブラウザから「見えていない」扱いになり、画面に入ったことも
// 遅延読み込みも検知されない。そのため写真の枠を見張り、読み込んでから幕を開ける。
const CLIPPED_IMAGE = 'img:is([data-reveal="image"], [data-reveal="wipe"])';

export function initMotion() {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  root.classList.add('motion-ready');

  const framedImages = new WeakMap<Element, HTMLImageElement[]>();
  const show = (el: HTMLElement) => {
    el.dataset.in = '';
  };

  const revealObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const target = entry.target as HTMLElement;
        revealObserver.unobserve(target);
        if (target.hasAttribute('data-reveal')) show(target);
        framedImages.get(target)?.forEach((img) => {
          img.loading = 'eager';
          img.decode().catch(() => {}).then(() => show(img));
        });
      }
    },
    { rootMargin: '0px 0px -10% 0px' },
  );

  // 画面に入る少し前から写真を読み込んでおく
  const preloadObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        preloadObserver.unobserve(entry.target);
        framedImages.get(entry.target)?.forEach((img) => {
          img.loading = 'eager';
        });
      }
    },
    { rootMargin: '0px 0px 600px 0px' },
  );

  const parallaxFrames = new WeakMap<Element, HTMLElement>();
  const visibleFrames = new Set<Element>();
  let ticking = false;
  const updateParallax = () => {
    ticking = false;
    const halfView = window.innerHeight / 2;
    visibleFrames.forEach((frame) => {
      // ページを移動して消えた枠は外す
      if (!frame.isConnected) {
        visibleFrames.delete(frame);
        return;
      }
      const el = parallaxFrames.get(frame)!;
      const rect = frame.getBoundingClientRect();
      // 枠が画面の中央にあるとき 0、画面の上下端に抜けるとき ±1
      const progress = (rect.top + rect.height / 2 - halfView) / (halfView + rect.height / 2);
      const amount = rect.height * (Number(el.dataset.parallax) || 0.08);
      const offset = -Math.max(-1, Math.min(1, progress)) * amount;
      el.style.setProperty('--parallax', `${offset.toFixed(1)}px`);
    });
  };
  const requestParallax = () => {
    if (ticking || reduceMotion.matches) return;
    ticking = true;
    requestAnimationFrame(updateParallax);
  };
  const parallaxObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) visibleFrames.add(entry.target);
      else visibleFrames.delete(entry.target);
    }
    requestParallax();
  });
  window.addEventListener('scroll', requestParallax, { passive: true });
  window.addEventListener('resize', requestParallax);

  const loopObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const el = entry.target as HTMLElement;
      if (entry.isIntersecting) el.dataset.visible = '';
      else delete el.dataset.visible;
    }
  });

  const scan = (scope: ParentNode) => {
    scope.querySelectorAll<HTMLElement>('[data-stagger]').forEach((list) => {
      // 入れ子のリストは親の演出にまかせる
      if (list.parentElement?.closest('[data-stagger]')) return;
      Array.from(list.children).forEach((child, i) => {
        const el = child as HTMLElement;
        if (!el.hasAttribute('data-reveal')) el.setAttribute('data-reveal', 'up');
        el.style.setProperty('--i', String(i));
      });
    });
    scope.querySelectorAll<HTMLElement>('[data-reveal]:not([data-in])').forEach((el) => {
      if (!el.matches(CLIPPED_IMAGE)) {
        revealObserver.observe(el);
        return;
      }
      const frame = el.parentElement!;
      const images = framedImages.get(frame) ?? [];
      if (!images.includes(el as HTMLImageElement)) images.push(el as HTMLImageElement);
      framedImages.set(frame, images);
      revealObserver.observe(frame);
      preloadObserver.observe(frame);
    });
    scope.querySelectorAll<HTMLElement>('[data-parallax]').forEach((el) => {
      parallaxFrames.set(el.parentElement!, el);
      parallaxObserver.observe(el.parentElement!);
    });
    scope.querySelectorAll('[data-loop]').forEach((el) => loopObserver.observe(el));
  };

  scan(document);
  new MutationObserver((records) => {
    for (const record of records) {
      record.addedNodes.forEach((node) => {
        if (node instanceof HTMLElement) scan(node.parentElement ?? node);
      });
    }
  }).observe(document.body, { childList: true, subtree: true });
}
